import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ArrowLeft } from 'lucide-react';

/** Where react-router keeps the number of the history entry now showing. */
const entryIndex = (): number => (window.history.state as { idx?: number } | null)?.idx ?? 0;

/**
 * The way back, for the one window that has none of its own.
 *
 * A desktop app window draws no browser chrome at all: no back arrow for an ordinary page
 * change, and none either once a shortcut has taken the window out to an application host --
 * there Chromium lends a thin out-of-scope strip, and that strip is gone the moment you are
 * home again. So the title bar keeps the arrow. Only the title bar: a phone already has the
 * system's edge-swipe, and a browser tab has the browser's own button.
 *
 * "Somewhere to go back to" is read off the history entry rather than counted here, because a
 * counter cannot survive the trip out to a host. React Router numbers every entry it creates,
 * so entry 0 is where this window's session began -- and leaving for a host and coming back
 * restores the entry, and its number, exactly as they were left.
 */
export function BackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const [canGoBack, setCanGoBack] = useState(false);

  useEffect(() => {
    const sync = () => setCanGoBack(entryIndex() > 0);
    sync();
    // Returning from a host restores this page from the back/forward cache, which re-runs no
    // effect of its own and changes no route: `pageshow` is the only word we get that it is back.
    window.addEventListener('pageshow', sync);
    return () => window.removeEventListener('pageshow', sync);
  }, [location.key]);

  return (
    <button type="button" className="btn btn-square btn-ghost btn-sm" onClick={() => void navigate(-1)} disabled={!canGoBack} aria-label="Back" title="Back">
      <ArrowLeft className="size-[1.125rem]" aria-hidden="true" />
    </button>
  );
}
